import { Button, Dialog, DialogBody, DialogContent, DialogFooter } from '@civilgrid/ui/components';
import { MAX_FIELD_NOTE_BODY_LENGTH } from '@civilgrid/utils/src/geoItems/fieldNoteBody';
import { formatCompassHeading } from '@civilgrid/utils/src/geoItems/formatCompassHeading';
import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { GeoItem } from '~/common/types';
import { Notify } from '~/components2/Notify';
import ZoomableImage from '~/components2/ZoomableImage';
import { useDeleteGeoItem, useEditBaseVersion, useUpdateGeoItem } from '~/pages/projectDetails/potholing/common';
import rootStore from '~/store';
import {
  captureFieldNotePhotoFullscreenToggled,
  captureFieldNotePhotoPanned,
  captureFieldNotePhotoZoomed,
} from './fieldNotePhotoAnalytics';
import store, { buildNoteBodyProps, type FieldNote, fieldNoteMutationErrorNotify } from './store';

export default function FieldNoteViewer() {
  const { detailItem } = useSnapshot(store);

  if (!detailItem) {
    return null;
  }

  // Remount on item change so local edit state resets when switching notes.
  return <FieldNoteViewerContent key={detailItem.id} detailItem={detailItem as FieldNote} />;
}

function FieldNoteViewerContent({ detailItem }: { detailItem: FieldNote }) {
  const isNote = detailItem.type === 'note';
  const isMedia = detailItem.type === 'image' || detailItem.type === 'video';

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(detailItem.body ?? '');
  const baseVersion = useEditBaseVersion(detailItem);

  const updateNote = useUpdateGeoItem({
    projectId: store.projectId ?? '',
    geoItemId: detailItem.id,
    baseVersion,
    onSuccess: () => {
      if (store.projectId) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectFieldNoteEdited, {
          project_id: store.projectId,
          organization_id: rootStore.currentOrganizationId || undefined,
          note_id: detailItem.id,
          note_type: 'note',
        });
        store.refreshFieldNotes(store.projectId);
      }
      Notify.info({ message: 'Field note updated' });
      store.setDetailItem(null);
    },
    onError: () => {
      Notify.error(fieldNoteMutationErrorNotify('update'));
    },
  });

  const deleteItem = useDeleteGeoItem({
    projectId: store.projectId ?? '',
    geoItemId: detailItem.id,
    baseVersion,
    onSuccess: () => {
      if (store.projectId) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectFieldNoteDeleted, {
          project_id: store.projectId,
          organization_id: rootStore.currentOrganizationId || undefined,
          note_id: detailItem.id,
          note_type: detailItem.type,
        });
        store.refreshFieldNotes(store.projectId);
      }
      Notify.info({ message: 'Field note deleted' });
      store.setDetailItem(null);
    },
    onError: () => {
      Notify.error(fieldNoteMutationErrorNotify('delete'));
    },
  });

  const onSave = () => {
    if (!store.projectId) return;
    const nextProps = buildNoteBodyProps((detailItem.props ?? []) as GeoItem['props'], draft);
    updateNote.mutate(nextProps);
  };

  const onDelete = () => {
    if (window.confirm('Are you sure you want to delete this field note?')) {
      deleteItem.mutate();
    }
  };

  // Caption (only meaningful for image/video — notes use `body` for content).
  const notes = isMedia ? detailItem.files?.[0]?.notes : undefined;

  let bodyView: JSX.Element | undefined;
  if (isNote && isEditing) {
    bodyView = (
      <textarea
        data-testid="field-note-body-textarea"
        value={draft}
        maxLength={MAX_FIELD_NOTE_BODY_LENGTH}
        onChange={(e) => setDraft(e.target.value)}
        className="h-[40vh] max-h-[60vh] w-[80vw] max-w-[90vw] resize-none overflow-auto whitespace-pre-wrap break-words rounded-md border border-grey-20 bg-white p-4 text-sm leading-relaxed text-grey-80 focus:outline-none focus:border-focus"
      />
    );
  } else if (isNote) {
    const body = detailItem.body?.trim();
    bodyView = body ? (
      <div className="max-h-[60vh] max-w-[90vw] overflow-auto whitespace-pre-wrap break-words rounded-md border border-grey-20 bg-grey-10 p-4 text-sm leading-relaxed text-grey-80">
        {body}
      </div>
    ) : (
      <div className="rounded-md border border-grey-20 bg-grey-10 p-4 text-sm italic text-grey-50">No content</div>
    );
  } else if (detailItem.type === 'video') {
    bodyView = (
      <video src={detailItem.files?.[0]?.path} controls autoPlay className="max-h-[60vh]">
        <track kind="captions" />
      </video>
    );
  } else if (detailItem.type === 'image') {
    bodyView = (
      <ZoomableImage
        src={detailItem.files?.[0]?.path ?? ''}
        alt="Field note attachment"
        // `h-` (not `max-h-`) is required: ZoomableImage's own `max-h-full` is a percentage, and
        // percentages only resolve against a parent with a definite (non-`auto`) height.
        className="h-[60vh] max-w-[90vw]"
        testIdPrefix="field-note-image"
        onFullscreenChange={(isFullscreen) =>
          captureFieldNotePhotoFullscreenToggled(
            {
              projectId: store.projectId,
              organizationId: rootStore.currentOrganizationId,
              noteId: detailItem.id,
            },
            isFullscreen,
          )
        }
        onZoomStep={(direction) =>
          captureFieldNotePhotoZoomed(
            {
              projectId: store.projectId,
              organizationId: rootStore.currentOrganizationId,
              noteId: detailItem.id,
            },
            direction,
          )
        }
        onPanStep={(direction, method) =>
          captureFieldNotePhotoPanned(
            {
              projectId: store.projectId,
              organizationId: rootStore.currentOrganizationId,
              noteId: detailItem.id,
            },
            direction,
            method,
          )
        }
      />
    );
  }
  // Unknown future types: no body view rendered. Metadata rows below still show.

  let location: string | undefined;
  if (detailItem.geometry?.coordinates) {
    const [lng, lat] = detailItem.geometry.coordinates;
    location = `${lng?.toFixed(6)}, ${lat?.toFixed(6)}`;
  }

  // Direction is only present on photo captures (mobile records compassHeading
  // per-file at shutter time); notes have no orientation.
  const direction = isMedia ? formatCompassHeading(detailItem.files?.[0]?.compassHeading) : undefined;

  const isMutating = updateNote.isPending || deleteItem.isPending;

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          store.setDetailItem(null);
        }
      }}
    >
      <DialogContent
        title="Field note"
        closeButtonProps={{ 'aria-label': 'Close field note', 'data-testid': 'field-note-close-button' }}
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="field-note-viewer-dialog"
      >
        <DialogBody>
          <div className="flex max-h-[90vh] max-w-[90vw] flex-col justify-start">
            <NoteItem label="Date" value={detailItem.createdAt?.toFormat('MMM dd, yyyy')} />
            <NoteItem label="Uploaded By" value={detailItem.createdBy?.name} />

            <NoteItem label="Notes" value={notes} />

            <NoteItem label="Location" value={location} />
            <NoteItem label="Direction" value={direction} />

            {detailItem.type === 'image' ? (
              bodyView
            ) : (
              <div className="flex max-h-[60vh] max-w-[90vw] justify-center overflow-auto">{bodyView}</div>
            )}
          </div>
        </DialogBody>

        {isNote && (
          <DialogFooter>
            {isEditing ? (
              <>
                <Button
                  variant="secondary"
                  data-testid="field-note-cancel-button"
                  disabled={isMutating}
                  onClick={() => {
                    setDraft(detailItem.body ?? '');
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="field-note-save-button"
                  disabled={draft === (detailItem.body ?? '') || isMutating}
                  loading={updateNote.isPending}
                  onClick={onSave}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="danger"
                  data-testid="field-note-delete-button"
                  disabled={isMutating}
                  loading={deleteItem.isPending}
                  onClick={onDelete}
                >
                  Delete
                </Button>
                <Button data-testid="field-note-edit-button" disabled={isMutating} onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface NoteItemProps {
  label?: string;
  value?: any;
}
const NoteItem = ({ label, value }: NoteItemProps) => {
  if (!label || !value) {
    return null;
  }

  return (
    <div className="text-sm font-noto">
      <span className="font-bold">{label}: </span>
      {value}
    </div>
  );
};
